import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Form, Modal } from '@surecompanies/core_components'
import { Select } from 'antd'
import isNil from 'lodash/isNil'
import { BulkGrantResultItem } from '../../api/dto/user-invitation.dto'
import {
  BulkApplyAll,
  BulkApplyAllLabel,
  GrantProgress,
  GrantRoleValue,
  GrantSectionHint,
  InviteModalForm,
} from './UserManagement.styles'
import { AccessResultList } from './AccessResultList'
import { GrantPlanSections } from './GrantPlanSections'
import { pluralize } from './userDisplay'
import { useRbacRoleOptions } from './useRbacRoleOptions'
import { failedEmails, GRANT_OUTCOMES } from './grantReport'
import { grantableRows } from './grantBuckets'
import type { GrantBuckets } from './grantBuckets'
import { buildSections, companyLabels } from './grantSections'

interface GrantAccessModalProps {
  open: boolean
  buckets: GrantBuckets
  confirmLoading: boolean
  /** Per-email outcomes once the run finishes. Null while still on the confirm step. */
  results: BulkGrantResultItem[] | null
  /** Recipients completed so far, for the in-flight progress line. */
  progress: { processed: number; total: number } | null
  onClose: () => void
  onSubmit: (roleIds: string[]) => void
  onRetryFailed: (emails: string[], roleIds: string[]) => void
}

/**
 * Confirms and reports a grant of finance access. The operator never picks the
 * action: recipients are split into what will happen to each of them, and only
 * the role is chosen. After submitting, the modal stays open and swaps to the
 * per-recipient outcome so a partial failure cannot be missed.
 */
const GrantAccessModal = ({
  open,
  buckets,
  confirmLoading,
  results,
  progress,
  onClose,
  onSubmit,
  onRetryFailed,
}: GrantAccessModalProps) => {
  const {
    options: roleOptions,
    isLoading: rolesLoading,
    isError: rolesError,
  } = useRbacRoleOptions()

  const [roleIds, setRoleIds] = useState<string[]>([])

  // Seed once per open transition. Depending on `buckets` would re-fire on any
  // background refetch and wipe an in-progress selection.
  const seededRef = useRef(false)
  useEffect(() => {
    if (!open) {
      seededRef.current = false
      return
    }
    if (seededRef.current || rolesLoading) return
    seededRef.current = true
    setRoleIds(roleOptions.length === 1 ? [roleOptions[0].value] : [])
  }, [open, roleOptions, rolesLoading])

  const actionableCount = grantableRows(buckets).length
  const sections = useMemo(() => buildSections(buckets), [buckets])
  const soleRole = roleOptions.length === 1 ? roleOptions[0] : null

  const retryable = useMemo(() => failedEmails(results), [results])

  // Every selected row, skipped ones included: the results report on all of
  // them, so a company is missing from exactly the rows that have none.
  const companiesByEmail = useMemo(
    () =>
      Object.fromEntries(
        [...grantableRows(buckets), ...buckets.skip.map(({ row }) => row)].map(
          (row) => [row.email, companyLabels(row)]
        )
      ),
    [buckets]
  )

  const canSubmit = actionableCount > 0 && !rolesError && roleIds.length > 0

  const handleOk = () => {
    if (!canSubmit) return
    onSubmit(roleIds)
  }

  const isDone = !isNil(results)

  const renderConfirm = () => (
    <InviteModalForm>
      <Form layout="vertical">
        <BulkApplyAll>
          <BulkApplyAllLabel>Role to grant</BulkApplyAllLabel>
          {/* One role is the norm here, and a picker with a single choice reads
              as a decision the operator does not have. */}
          {soleRole ? (
            <GrantRoleValue>{soleRole.label}</GrantRoleValue>
          ) : (
            <Select<string[]>
              mode="multiple"
              allowClear
              loading={rolesLoading}
              disabled={rolesError}
              style={{ width: '100%' }}
              placeholder="Select role(s)"
              value={roleIds}
              onChange={setRoleIds}
              options={roleOptions}
              optionFilterProp="label"
              status={rolesError ? 'error' : undefined}
            />
          )}
          {rolesError ? (
            <GrantSectionHint>
              Could not load roles. Close and try again.
            </GrantSectionHint>
          ) : null}
        </BulkApplyAll>

        <GrantPlanSections sections={sections} />

        {progress ? (
          <GrantProgress aria-live="polite">
            Processed {progress.processed} of {progress.total}…
          </GrantProgress>
        ) : null}
      </Form>
    </InviteModalForm>
  )

  const resultFooter = [
    retryable.length > 0 ? (
      <Button
        key="retry"
        loading={confirmLoading}
        onClick={() => onRetryFailed(retryable, roleIds)}
      >
        Retry failed ({retryable.length})
      </Button>
    ) : null,
    // Same reason as the close gates below: a retry in flight has to land or
    // its outcomes are lost.
    <Button
      key="done"
      type="primary"
      disabled={confirmLoading}
      onClick={onClose}
    >
      Done
    </Button>,
  ]

  return (
    <Modal
      title={
        isDone
          ? 'Grant Finance Manager results'
          : 'Grant Finance Manager access'
      }
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText={
        actionableCount > 0
          ? `Grant access to ${pluralize(actionableCount, 'user')}`
          : 'Grant access'
      }
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !canSubmit }}
      cancelButtonProps={{ disabled: confirmLoading }}
      footer={isDone ? resultFooter : undefined}
      width={640}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      destroyOnClose
      // The report only exists while this modal is open, so walking away from a
      // run in flight would hide which recipients failed. Every close route has
      // to wait for the run to land.
      closable={!confirmLoading}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
    >
      {isDone ? (
        <AccessResultList
          items={results ?? []}
          outcomes={GRANT_OUTCOMES}
          companiesByEmail={companiesByEmail}
          progress={progress}
          progressLabel="Retrying"
        />
      ) : (
        renderConfirm()
      )}
    </Modal>
  )
}

export { GrantAccessModal }
